import React, { useState, useRef, useMemo } from 'react'
import {
  Modal,
  Button,
  Table,
  Alert,
  Form,
  Spinner,
  Badge,
  ProgressBar,
} from 'react-bootstrap'
import {
  FaFileExcel,
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
} from 'react-icons/fa'
import * as XLSX from 'xlsx'
import apinode from '../../services/apinode'

/**
 * ModalImportarPlanilha
 *
 * Lê uma planilha (.xlsx / .xls / .csv) com colunas de SKU, Preço e Estoque
 * e aplica os ajustes em massa via API.
 *
 * Colunas reconhecidas (case/acento-insensitive):
 *   SKU      -> sku, codigo, cod
 *   Preço    -> preco, price, valor
 *   Estoque  -> estoque, quantidade, qtd, qtde, stock
 *
 * Endpoints usados:
 *   ESTOQUE: POST /anuncios/ajustes-em-massa/:sku/:idconta?seller=:seller   body { quantidade }
 *            (já existe no seu backend)
 *   PREÇO:   POST /anuncios/preco-em-massa/:sku/:idconta?seller=:seller      body { preco }
 *            (VOCÊ PRECISA CRIAR ESSA ROTA — ver observação no final do arquivo)
 */

const norm = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

const COLS = {
  sku: ['sku', 'codigo', 'cod', 'codigosku'],
  preco: ['preco', 'price', 'valor', 'precovenda'],
  estoque: ['estoque', 'quantidade', 'qtd', 'qtde', 'stock', 'quant'],
}

function detectColumn(headers, candidates) {
  const normalized = headers.map((h) => ({ raw: h, n: norm(h) }))
  for (const c of candidates) {
    const hit = normalized.find((h) => h.n === c || h.n.includes(c))
    if (hit) return hit.raw
  }
  return null
}

function parsePreco(v) {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return v
  let s = String(v)
    .trim()
    .replace(/[R$\s]/gi, '')
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.')
  } else if (s.includes(',')) {
    s = s.replace(',', '.')
  }
  const n = Number(s)
  return Number.isNaN(n) ? null : n
}

function parseQtd(v) {
  if (v === null || v === undefined || v === '') return null
  const n = parseInt(String(v).replace(/[^\d-]/g, ''), 10)
  return Number.isNaN(n) ? null : n
}

export function ModalImportarPlanilha({
  show,
  onClose,
  contaSelecionada,
  onFinish, // callback opcional p/ recarregar a lista ao terminar
}) {
  const fileInputRef = useRef(null)
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState([]) // [{ sku, preco, estoque }]
  const [parseError, setParseError] = useState('')
  const [aplicarEstoque, setAplicarEstoque] = useState(true)
  const [aplicarPreco, setAplicarPreco] = useState(false)

  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState(null) // { success: [], errors: [] }

  const temColunaPreco = useMemo(
    () => rows.some((r) => r.preco !== null && r.preco !== undefined),
    [rows],
  )
  const temColunaEstoque = useMemo(
    () => rows.some((r) => r.estoque !== null && r.estoque !== undefined),
    [rows],
  )

  const reset = () => {
    setFileName('')
    setRows([])
    setParseError('')
    setResults(null)
    setProgress(0)
    setIsProcessing(false)
    setAplicarEstoque(true)
    setAplicarPreco(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    if (isProcessing) return
    reset()
    onClose()
    if (results && onFinish) onFinish()
  }

  const handleFile = async (file) => {
    if (!file) return
    setParseError('')
    setResults(null)
    setFileName(file.name)

    try {
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' })

      if (!json.length) {
        setParseError('A planilha está vazia.')
        setRows([])
        return
      }

      const headers = Object.keys(json[0])
      const colSku = detectColumn(headers, COLS.sku)
      const colPreco = detectColumn(headers, COLS.preco)
      const colEstoque = detectColumn(headers, COLS.estoque)

      if (!colSku) {
        setParseError(
          'Não encontrei uma coluna de SKU. Renomeie o cabeçalho para "SKU".',
        )
        setRows([])
        return
      }

      const parsed = json
        .map((r) => ({
          sku: String(r[colSku] ?? '').trim(),
          preco: colPreco ? parsePreco(r[colPreco]) : null,
          estoque: colEstoque ? parseQtd(r[colEstoque]) : null,
        }))
        .filter((r) => r.sku) // descarta linhas sem SKU

      if (!parsed.length) {
        setParseError('Nenhuma linha válida (todas sem SKU).')
        setRows([])
        return
      }

      setRows(parsed)
      setAplicarEstoque(Boolean(colEstoque))
      setAplicarPreco(Boolean(colPreco))
    } catch (err) {
      console.error('Erro ao ler planilha:', err)
      setParseError('Não consegui ler o arquivo. Use .xlsx, .xls ou .csv.')
      setRows([])
    }
  }

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx))

  const handleProcess = async () => {
    if (!rows.length) return
    setIsProcessing(true)
    setProgress(0)

    const success = []
    const errors = []

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const acoes = []

      try {
        if (aplicarEstoque && row.estoque !== null) {
          await apinode.post(
            `/anuncios/ajustes-em-massa/${encodeURIComponent(row.sku)}/${
              localStorage.idconta
            }?seller=${contaSelecionada}`,
            { quantidade: row.estoque },
          )
          acoes.push(`estoque=${row.estoque}`)
        }

        if (aplicarPreco && row.preco !== null) {
          await apinode.post(
            `/anuncios/preco-em-massa/${encodeURIComponent(row.sku)}/${
              localStorage.idconta
            }?seller=${contaSelecionada}`,
            { preco: row.preco },
          )
          acoes.push(
            `preço=${row.preco.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}`,
          )
        }

        if (acoes.length) {
          success.push({ sku: row.sku, info: acoes.join(' · ') })
        }
      } catch (err) {
        errors.push({
          sku: row.sku,
          error:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Falha ao aplicar ajuste',
        })
      }

      setProgress(Math.round(((i + 1) / rows.length) * 100))
    }

    setResults({ success, errors })
    setIsProcessing(false)
  }

  const podeProcessar =
    rows.length > 0 &&
    ((aplicarEstoque && temColunaEstoque) ||
      (aplicarPreco && temColunaPreco))

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop={isProcessing ? 'static' : true}
    >
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title style={{ fontSize: 18, display: 'flex', gap: 8 }}>
          <FaFileExcel style={{ color: '#1d6f42' }} />
          Importar planilha — preço e estoque
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* RESULTADO */}
        {results ? (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 12,
                  padding: 16,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{ fontSize: 26, fontWeight: 700, color: '#059669' }}
                >
                  {results.success.length}
                </div>
                <small style={{ color: '#047857' }}>Sucessos</small>
              </div>
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 12,
                  padding: 16,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{ fontSize: 26, fontWeight: 700, color: '#dc2626' }}
                >
                  {results.errors.length}
                </div>
                <small style={{ color: '#b91c1c' }}>Falhas</small>
              </div>
            </div>

            {results.errors.length > 0 && (
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                <Table
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((e, i) => (
                      <tr key={`${e.sku}-${i}`}>
                        <td>
                          <FaTimesCircle
                            style={{ color: '#dc2626', marginRight: 6 }}
                          />
                          {e.sku}
                        </td>
                        <td style={{ color: '#6b7280', fontSize: 13 }}>
                          {e.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {results.errors.length === 0 && (
              <Alert variant="success">
                <FaCheckCircle className="me-2" />
                Todos os {results.success.length} SKUs foram ajustados com
                sucesso.
              </Alert>
            )}
          </div>
        ) : isProcessing ? (
          /* PROGRESSO */
          <div className="text-center py-4">
            <Spinner
              animation="border"
              style={{ color: '#667eea' }}
            />
            <div className="mt-3 mb-2">Processando {rows.length} SKUs...</div>
            <ProgressBar
              now={progress}
              label={`${progress}%`}
              animated
              style={{ height: 22 }}
            />
            <small className="text-muted d-block mt-2">
              Não feche esta janela até concluir.
            </small>
          </div>
        ) : (
          /* UPLOAD + PREVIEW */
          <>
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                handleFile(e.dataTransfer.files?.[0])
              }}
              style={{
                border: '2px dashed #cbd5e1',
                borderRadius: 14,
                padding: 28,
                textAlign: 'center',
                cursor: 'pointer',
                background: '#f8fafc',
                transition: 'all .2s ease',
              }}
            >
              <FaUpload size={26} style={{ color: '#667eea' }} />
              <div style={{ marginTop: 10, fontWeight: 600 }}>
                {fileName || 'Clique ou arraste a planilha aqui'}
              </div>
              <small className="text-muted">
                Formatos: .xlsx, .xls, .csv — colunas: SKU, Preço, Estoque
              </small>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            {parseError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {parseError}
              </Alert>
            )}

            {rows.length > 0 && (
              <>
                <div className="d-flex align-items-center gap-3 mt-3 mb-2">
                  <Form.Check
                    type="switch"
                    id="sw-estoque"
                    label="Aplicar estoque"
                    checked={aplicarEstoque}
                    disabled={!temColunaEstoque}
                    onChange={(e) => setAplicarEstoque(e.target.checked)}
                  />
                  <Form.Check
                    type="switch"
                    id="sw-preco"
                    label="Aplicar preço"
                    checked={aplicarPreco}
                    disabled={!temColunaPreco}
                    onChange={(e) => setAplicarPreco(e.target.checked)}
                  />
                  <Badge bg="secondary" className="ms-auto">
                    {rows.length} linhas
                  </Badge>
                </div>

                <div
                  style={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                  }}
                >
                  <Table
                    size="sm"
                    hover
                    className="mb-0 align-middle"
                  >
                    <thead
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: '#f8fafc',
                      }}
                    >
                      <tr>
                        <th>SKU</th>
                        <th className="text-end">Preço</th>
                        <th className="text-end">Estoque</th>
                        <th style={{ width: 40 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={`${r.sku}-${i}`}>
                          <td>{r.sku}</td>
                          <td className="text-end">
                            {r.preco !== null
                              ? r.preco.toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })
                              : '—'}
                          </td>
                          <td className="text-end">
                            {r.estoque !== null ? r.estoque : '—'}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              onClick={() => removeRow(i)}
                              title="Remover linha"
                            >
                              <FaTrash size={13} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <Alert variant="warning" className="mt-3 mb-0">
                  Processo sequencial e <strong>irreversível</strong>. Confira
                  os valores antes de confirmar.
                </Alert>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {results ? (
          <Button variant="primary" onClick={handleClose}>
            Concluir
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleProcess}
              disabled={!podeProcessar || isProcessing}
            >
              {isProcessing ? 'Processando...' : `Aplicar (${rows.length})`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

/*
 * --------------------------------------------------------------------------
 * BACKEND — rota de preço por SKU que você precisa criar (espelhando a de
 * estoque). Exemplo Express:
 *
 *   router.post('/anuncios/preco-em-massa/:sku/:idconta', async (req, res) => {
 *     const { sku, idconta } = req.params
 *     const { seller } = req.query
 *     const { preco } = req.body
 *     // 1. buscar todos os anúncios/variações com esse SKU para o seller
 *     // 2. chamar a API do ML para atualizar o price de cada um
 *     // 3. retornar { resultadoAnuncios, resultadoVariantes } (ou erro)
 *   })
 *
 * Se ainda não tiver, deixe "Aplicar preço" desligado e use só estoque.
 * --------------------------------------------------------------------------
 */